import React from 'react'
import { Button, Div, Flex, Text } from '@sendoutcards/quantum-design-ui'
import { MainInfo, SupplementalInfo } from '../components'
import { AddExpenseContainer, PointsPackagesContainer } from './index'
import ExpensePurchaseModal, {
  StripeOrderDetailsType,
} from 'src/payments/components/ExpenseBucket/ExpensePurchaseModal'
import styles from './purchasePoints.module.scss'
import { useActions, useMutations, useState } from 'src/hooks'

const PurchasePoints: React.FC = () => {
  const actions = useActions()
  const mutations = useMutations()

  const [pointOption, setPointOption] = useState<number>(0)
  const [pointAmount, setPointAmount] = useState<number>(0)
  const [pricePerPoint, setPricePerPoint] = useState<number>(0)
  const [expenseAmount, setExpenseAmount] = useState<number>(0)
  const [isExpenseAdded, setIsExpenseAdded] = useState(false)
  const [stripeOrderId, setStripeOrderId] = useState<string | null>(null)
  const [stripeOrderDetails, setStripeOrderDetails] = useState<
    StripeOrderDetailsType
  >({ tax: '', subtotal: '', total: '' })
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false)
  const [isPurchasingProduct, setIsPurchasingProduct] = useState(false)

  type PurchaseItemType = {
    itemCode: string
    quantity: number
  }

  const getPointsItemCode = (pricePerPoint: number): string => {
    switch (pricePerPoint) {
      case 0.31:
        return '11092'
      case 0.39:
        return '11119'
      case 0.43:
        return '11129'
      case 0.49:
        return '11128'
      case 0.59:
        return '11127'
      case 0.66:
        return '11125'
      default:
        return ''
    }
  }

  const isChargeBelowMinimum = (): boolean => {
    return expenseAmount + pricePerPoint * pointAmount < 0.5
  }

  const getItemsToPurchase = (): PurchaseItemType[] => {
    const EXPENSE_ITEM_CODE = '11132'

    const pointsToPurchase: PurchaseItemType | null =
      pricePerPoint && pointAmount
        ? {
            itemCode: getPointsItemCode(pricePerPoint),
            quantity: pointAmount,
          }
        : null

    const expenseToPurchase: PurchaseItemType | null =
      isExpenseAdded && expenseAmount
        ? {
            itemCode: EXPENSE_ITEM_CODE,
            quantity: expenseAmount,
          }
        : null

    const itemsToPurchase: PurchaseItemType[] = [
      ...(pointsToPurchase ? [pointsToPurchase] : []),
      ...(expenseToPurchase ? [expenseToPurchase] : []),
    ]
    return itemsToPurchase
  }

  const purchasePointsExpense = async () => {
    const itemsToPurchase = getItemsToPurchase()

    setIsPurchasingProduct(true)
    const {
      createStripeOrder: { stripeOrder },
    } = await mutations.createStripeOrder({
      itemsToPurchase: itemsToPurchase,
      coupon: undefined,
    })
    setStripeOrderId(stripeOrder.id)
    setStripeOrderDetails({
      tax: stripeOrder.tax,
      total: stripeOrder.total,
      subtotal: stripeOrder.subtotal,
    })
    setIsPurchaseModalOpen(true)
    setIsPurchasingProduct(false)
  }

  const submitStripeOrder = async () => {
    if (stripeOrderId) {
      setIsPurchasingProduct(true)
      try {
        const { purchaseProduct } = await mutations.purchaseProduct({
          orderId: stripeOrderId,
        })
        const { error } = purchaseProduct
        if (!!error) {
          console.error(error)
        } else {
          actions.updatedAccount(purchaseProduct.account)
          setIsPurchaseModalOpen(false)
          setIsPurchasingProduct(false)
          setStripeOrderId(null)
          setStripeOrderDetails({ tax: '', total: '', subtotal: '' })
        }
      } catch (error) {
        setIsPurchasingProduct(false)
        console.error(error)
      }
    }
  }

  const buttonTitle = (): string =>
    isPurchaseModalOpen && isPurchasingProduct
      ? 'Purchasing...'
      : isPurchasingProduct
      ? 'Creating order...'
      : !isPurchasingProduct
      ? 'Continue'
      : 'Done'

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        width="95%"
        className={styles.container}
      >
        <MainInfo />
        <PointsPackagesContainer
          pointOption={pointOption}
          setPointOption={setPointOption}
          setPointAmount={setPointAmount}
          setPricePerPoint={setPricePerPoint}
        />
        <AddExpenseContainer
          setExpenseAmount={setExpenseAmount}
          setIsExpenseAdded={setIsExpenseAdded}
          isExpenseAdded={isExpenseAdded}
        />
        <Div className={styles.buttons}>
          <Button
            disabled={
              (!pointAmount && !expenseAmount) ||
              isPurchasingProduct ||
              isChargeBelowMinimum()
            }
            onClick={() => purchasePointsExpense()}
            title={buttonTitle()}
          />
          <Text type="body">or</Text>
          <Button onClick={actions.openPricing}>
            View Subscription Options
          </Button>
        </Div>
        <SupplementalInfo />
      </Flex>
      {isPurchaseModalOpen && (
        <ExpensePurchaseModal
          onPurchase={submitStripeOrder}
          onClose={() => setIsPurchaseModalOpen(false)}
          isPurchasing={isPurchasingProduct}
          stripeOrderDetails={stripeOrderDetails}
        />
      )}
    </>
  )
}

export default PurchasePoints
